/**
 * Right-docked detail sidebar for the case knowledge graph.
 *
 * Clicking a node or relationship reveals a tabbed detail UI docked into the
 * graph layout.
 */

import { Loader2, MapPin, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useDocumentOverlayOptional } from '@/components/document-overlay/overlay-context'
import { PassageDetailSection } from '@/components/investigations/entity-detail/PassageDetailSection'
import { isPassage, passageDetail } from '@/components/investigations/entity-detail/passage-detail'
import { LABEL_COLORS } from '@/components/neo4j/cytoscape/entity-icons'
import type { SelectedEdge, SelectedNode } from '@/components/neo4j/cytoscape/types'
import { NeighborsTab } from '@/components/neo4j/shared/entity-details/NeighborsTab'
import { PropertiesTab } from '@/components/neo4j/shared/entity-details/PropertiesTab'
import { RelationshipsTab } from '@/components/neo4j/shared/entity-details/RelationshipsTab'
import { SourceDocumentsTab } from '@/components/neo4j/shared/entity-details/SourceDocumentsTab'
import type {
  NeighborEdge,
  NeighborNode,
  RelationshipSummary,
} from '@/components/neo4j/shared/entity-details/types'
import { useEntityDetails } from '@/components/neo4j/shared/useEntityDetails'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { OsintEntityLaunchButton } from '@/features/osint/OsintEntityLaunchButton'
import { EdgeRowsButton } from '@/features/structured-ingest/EdgeRowsButton'
import { AudioEvidence } from '@/features/voices/AudioEvidence'
import { hasGeoCoordinates } from '@/utils/geo'

interface LegendLabel {
  label: string
  count: number
  color: string
  borderColor?: string
}

export interface KnowledgeGraphDetailSidebarProps {
  caseId: string
  open: boolean
  onClose: () => void
  selectedNode: SelectedNode | null
  selectedEdge: SelectedEdge | null
  legendLabels?: LegendLabel[]
  relationships: RelationshipSummary[]
  onAddToGraph: (nodes: NeighborNode[], edges: NeighborEdge[]) => void
  onDeleteEntity?: (nodeId: string) => Promise<void> | void
}

type TabKey = 'properties' | 'relationships' | 'neighbours' | 'sources'

function labelStyle(label: string, legend: LegendLabel[] | undefined): React.CSSProperties {
  const hit = legend?.find(l => l.label === label)
  if (hit) {
    return {
      backgroundColor: hit.color,
      color: 'var(--primary-foreground)',
      borderColor: hit.borderColor ?? hit.color,
    }
  }
  const colors = LABEL_COLORS[label] ?? LABEL_COLORS.Default
  if (!colors) {
    return { backgroundColor: 'var(--muted)', color: 'var(--foreground)' }
  }
  return { backgroundColor: colors.fill, color: '#ffffff', borderColor: colors.stroke }
}

export function KnowledgeGraphDetailSidebar({
  caseId,
  open,
  onClose,
  selectedNode,
  selectedEdge,
  legendLabels,
  relationships,
  onAddToGraph,
  onDeleteEntity,
}: KnowledgeGraphDetailSidebarProps) {
  const { fullNodeDetails, isLoading: isLoadingNodeDetails } = useEntityDetails({
    caseId,
    selectedNode,
    selectedEdge,
  })

  const isEdge = Boolean(selectedEdge && !selectedNode)
  // Opens a case document over the page; null outside the case shell.
  const overlay = useDocumentOverlayOptional()

  const [tab, setTab] = useState<TabKey>('properties')

  useEffect(() => {
    setTab('properties')
  }, [selectedEdge?.id, selectedNode?.id])

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const deletingRef = useRef(false)
  deletingRef.current = deleting
  const canDelete = Boolean(onDeleteEntity) && Boolean(selectedNode) && !isEdge

  const handleDelete = useCallback(async () => {
    if (!selectedNode || !onDeleteEntity) return
    setDeleting(true)
    try {
      await onDeleteEntity(selectedNode.id)
      setConfirmDeleteOpen(false)
      onClose()
    } catch (err) {
      console.error('Delete entity failed:', err)
      toast.error('Failed to delete entity')
    } finally {
      setDeleting(false)
    }
  }, [selectedNode, onDeleteEntity, onClose])

  if (!open || (!selectedNode && !selectedEdge)) return null

  const titleLabel = selectedNode
    ? (selectedNode.typeLabel ?? selectedNode.labels?.[0] ?? 'Entity')
    : selectedEdge
      ? selectedEdge.type
      : 'Details'

  const title = selectedNode
    ? selectedNode.name || selectedNode.id
    : selectedEdge
      ? selectedEdge.type === 'POSSIBLE_MATCH'
        ? 'Entity Match'
        : `${selectedEdge.sourceName ?? selectedEdge.source} → ${selectedEdge.targetName ?? selectedEdge.target}`
      : 'Details'

  const sourceDocs = (fullNodeDetails as { source_documents?: unknown[] } | null)?.source_documents

  const nodeProps =
    (fullNodeDetails as { properties?: Record<string, unknown> } | null)?.properties ??
    selectedNode?.properties
  const hasGeo = Boolean(selectedNode) && hasGeoCoordinates(nodeProps)

  // A transcript passage an expand put on the canvas is read here, from its own
  // properties, and heard in its recording — as in the investigation panel
  // (SidebarEntityDetail): a strip of transcript and the way to the recording.
  // Whose each voice is comes with the node's read, beside the properties (`speakers`).
  const passage =
    selectedNode && isPassage(selectedNode.labels)
      ? passageDetail(caseId, nodeProps ?? {}, fullNodeDetails?.speakers)
      : null

  return (
    <aside className="flex w-full shrink-0 flex-col border-border bg-background max-lg:absolute max-lg:inset-0 max-lg:z-40 lg:w-[420px] lg:border-l">
      <header className="flex items-center gap-3 border-b border-border px-4 py-3">
        <span
          className="rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
          style={labelStyle(titleLabel, legendLabels)}
        >
          {titleLabel}
        </span>
        {hasGeo && (
          <span
            className="flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground"
            title="Has location data"
          >
            <MapPin className="h-3 w-3" />
            Geo
          </span>
        )}
        <h2
          className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground max-md:line-clamp-2 max-md:whitespace-normal max-md:break-words"
          title={title}
        >
          {title}
        </h2>
        {canDelete && (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setConfirmDeleteOpen(true)}
            aria-label="Delete entity"
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
        <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Close details">
          <X className="h-4 w-4" />
        </Button>
      </header>

      {selectedEdge && isEdge && Boolean(selectedEdge.properties?._projection_id) && (
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
          <EdgeRowsButton caseId={caseId} edge={selectedEdge} />
        </div>
      )}

      {selectedNode && !isEdge && (
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2 max-lg:empty:hidden">
          <OsintEntityLaunchButton
            target={{
              caseId,
              entityId: selectedNode.id,
              entityName: selectedNode.name,
              // The badge in the header above — the button follows what the
              // analyst is being told this node is.
              entityType: titleLabel,
            }}
          />
        </div>
      )}

      <Tabs
        value={tab}
        onValueChange={v => setTab(v as TabKey)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <TabsList
          variant="line"
          className="h-9 w-full shrink-0 justify-start rounded-none border-b border-border bg-transparent px-3 max-md:group-data-horizontal/tabs:h-11 max-md:pr-8 max-md:[mask-image:linear-gradient(to_right,black_calc(100%-2rem),transparent)]"
        >
          <TabsTrigger value="properties" className="whitespace-nowrap">
            Properties
          </TabsTrigger>
          <TabsTrigger value="relationships" className="whitespace-nowrap">
            Relationships
          </TabsTrigger>
          {!isEdge ? (
            <TabsTrigger value="neighbours" className="whitespace-nowrap">
              Neighbours
            </TabsTrigger>
          ) : null}
          {!isEdge ? (
            <TabsTrigger value="sources" className="whitespace-nowrap">
              Source Documents
            </TabsTrigger>
          ) : null}
        </TabsList>

        <div className="min-h-0 flex-1 overflow-hidden">
          <TabsContent value="properties" className="mt-0 h-full overflow-y-auto p-3">
            {selectedNode && (
              <div className="mb-3 empty:hidden">
                <AudioEvidence
                  caseId={caseId}
                  labels={fullNodeDetails?.labels ?? selectedNode.labels}
                  properties={fullNodeDetails?.properties ?? selectedNode.properties}
                  onOpenDocument={overlay?.open ?? null}
                />
              </div>
            )}
            {passage ? (
              <div className="mb-3 border-b border-border pb-3 empty:hidden">
                <PassageDetailSection passage={passage} onOpenDocument={overlay?.open ?? null} />
              </div>
            ) : null}
            <PropertiesTab
              caseId={caseId}
              selectedNode={selectedNode}
              selectedEdge={selectedEdge}
              fullNodeDetails={fullNodeDetails ?? null}
              isLoadingNodeDetails={isLoadingNodeDetails}
              legendLabels={legendLabels}
            />
          </TabsContent>
          <TabsContent value="relationships" className="mt-0 h-full overflow-y-auto p-3">
            <RelationshipsTab
              selectedNodeId={selectedNode?.id ?? null}
              relationships={relationships}
              legendLabels={legendLabels}
            />
          </TabsContent>
          {!isEdge ? (
            <TabsContent value="neighbours" className="mt-0 h-full overflow-y-auto p-3">
              <NeighborsTab
                caseId={caseId}
                nodeId={selectedNode?.id ?? null}
                legendLabels={legendLabels}
                onAddToGraph={onAddToGraph}
              />
            </TabsContent>
          ) : null}
          {!isEdge ? (
            <TabsContent value="sources" className="mt-0 h-full overflow-y-auto p-3">
              <SourceDocumentsTab
                sourceDocuments={
                  (sourceDocs as
                    | Array<{ id?: string; name?: string; path?: string }>
                    | undefined) ?? null
                }
              />
            </TabsContent>
          ) : null}
        </div>
      </Tabs>

      <AlertDialog
        open={confirmDeleteOpen}
        onOpenChange={open => !deleting && setConfirmDeleteOpen(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete entity?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{' '}
              <span className="font-semibold text-foreground">{title}</span> and all its
              relationships from the case graph. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={e => {
                e.preventDefault()
                void handleDelete()
              }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive/50"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Deleting…
                </>
              ) : (
                <>
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                  Delete
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  )
}
